import React, { useState, useEffect, useRef } from 'react';
import { apiFetch, getApiBaseUrl } from '../../../utils/apiConfig';
import { getAuthHeaders } from '../../../utils/authUtils';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from '../../CentralSnackbar';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  IconButton,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert,
  Tooltip,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Chip,
  FormControlLabel,
  Switch,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Slider,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Autocomplete,
  Radio,
  RadioGroup,
  FormLabel,
  Stack
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIcon,
  ExpandMore as ExpandMoreIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Preview as PreviewIcon,
  Settings as SettingsIcon,
  Remove as RemoveIcon,
  ContentCopy as CopyIcon,
  VisibilityOff as VisibilityOffIcon,
  Visibility as VisibilityIcon,
  TextFieldsOutlined as FreetextIcon
} from '@mui/icons-material';


const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:25000';

// Vollständige DICOM-Modalitätenliste aus CalendarSettings.js
const ALL_MODALITIES = [
  // Standard-Modalitäten
  { id: 1, code: 'DX', name: 'Digitale Radiographie' },
  { id: 2, code: 'CT', name: 'Computertomographie' },
  { id: 3, code: 'MR', name: 'Magnetresonanztomographie' },
  { id: 4, code: 'RF', name: 'Radiofluoroskopie' },
  { id: 5, code: 'XA', name: 'Röntgen-Angiographie' },
  { id: 6, code: 'MG', name: 'Mammographie' },
  { id: 7, code: 'US', name: 'Ultraschall' },
  // Erweiterte Modalitäten (alphabetisch)
  { id: 8, code: 'AR', name: 'Autorefraktion' },
  { id: 9, code: 'ASMT', name: 'Inhaltsbewertungsergebnisse' },
  { id: 10, code: 'AU', name: 'Audio' },
  { id: 11, code: 'BDUS', name: 'Knochendichtemessung (Ultraschall)' },
  { id: 12, code: 'BI', name: 'Biomagnetische Bildgebung' },
  { id: 13, code: 'BMD', name: 'Knochendichtemessung (Röntgen)' },
  { id: 14, code: 'CR', name: 'Computed Radiography' },
  { id: 15, code: 'CTPROTOCOL', name: 'CT-Protokoll (durchgeführt)' },
  { id: 16, code: 'DG', name: 'Diaphanographie' },
  { id: 17, code: 'DOC', name: 'Dokument' },
  { id: 18, code: 'ECG', name: 'Elektrokardiographie' },
  { id: 19, code: 'EPS', name: 'Kardiale Elektrophysiologie' },
  { id: 20, code: 'ES', name: 'Endoskopie' },
  { id: 21, code: 'FID', name: 'Referenzmarker' },
  { id: 22, code: 'GM', name: 'Allgemeine Mikroskopie' },
  { id: 23, code: 'HC', name: 'Hardcopy' },
  { id: 24, code: 'HD', name: 'Hämodynamische Wellenform' },
  { id: 25, code: 'IO', name: 'Intraorale Radiographie' },
  { id: 26, code: 'IOL', name: 'Intraokuläre Linsendaten' },
  { id: 27, code: 'IVOCT', name: 'Intravaskuläre optische Kohärenztomographie' },
  { id: 28, code: 'IVUS', name: 'Intravaskulärer Ultraschall' },
  { id: 29, code: 'KER', name: 'Keratometrie' },
  { id: 30, code: 'KO', name: 'Schlüsselobjektauswahl' },
  { id: 31, code: 'LEN', name: 'Linsenmessung' },
  { id: 32, code: 'LS', name: 'Laser-Oberflächenscan' },
  { id: 33, code: 'M3D', name: 'Modell für 3D-Fertigung' },
  { id: 34, code: 'NM', name: 'Nuklearmedizin' },
  { id: 35, code: 'OAM', name: 'Ophthalmische Axialmessungen' },
  { id: 36, code: 'OCT', name: 'Optische Kohärenztomographie (nicht-ophthalmisch)' },
  { id: 37, code: 'OP', name: 'Ophthalmische Fotographie' },
  { id: 38, code: 'OPM', name: 'Ophthalmische Kartierung' },
  { id: 39, code: 'OPT', name: 'Ophthalmische Tomographie' },
  { id: 40, code: 'OPTBSV', name: 'Ophthalmische Tomographie B-Scan-Volumenanalyse' },
  { id: 41, code: 'OPTENF', name: 'Ophthalmische Tomographie En Face' },
  { id: 42, code: 'OPV', name: 'Ophthalmisches Gesichtsfeld' },
  { id: 43, code: 'OSS', name: 'Optischer Oberflächenscan' },
  { id: 44, code: 'OT', name: 'Andere' },
  { id: 45, code: 'PLAN', name: 'Plan' },
  { id: 46, code: 'PR', name: 'Präsentationsstatus' },
  { id: 47, code: 'PT', name: 'Positronen-Emissions-Tomographie' },
  { id: 48, code: 'PX', name: 'Panorama-Röntgen' },
  { id: 49, code: 'REG', name: 'Registrierung' },
  { id: 50, code: 'RESP', name: 'Atemwellenform' },
  { id: 51, code: 'RG', name: 'Radiographische Bildgebung (konventioneller Film/Bildschirm)' },
  { id: 52, code: 'RTDOSE', name: 'Strahlentherapie-Dosis' },
  { id: 53, code: 'RTIMAGE', name: 'Strahlentherapie-Bild' },
  { id: 54, code: 'RTINTENT', name: 'Strahlentherapie-Absicht' },
  { id: 55, code: 'RTPLAN', name: 'Strahlentherapie-Plan' },
  { id: 56, code: 'RTRAD', name: 'RT-Strahlung' },
  { id: 57, code: 'RTRECORD', name: 'RT-Behandlungsprotokoll' },
  { id: 58, code: 'RTSEGANN', name: 'Strahlentherapie-Segmentannotation' },
  { id: 59, code: 'RTSTRUCT', name: 'Strahlentherapie-Struktursatz' },
  { id: 60, code: 'RWV', name: 'Real-World-Wert-Karte' },
  { id: 61, code: 'SEG', name: 'Segmentierung' },
  { id: 62, code: 'SM', name: 'Objektträger-Mikroskopie' },
  { id: 63, code: 'SMR', name: 'Stereometrische Beziehung' },
  { id: 64, code: 'SR', name: 'SR-Dokument' },
  { id: 65, code: 'SRF', name: 'Subjektive Refraktion' },
  { id: 66, code: 'STAIN', name: 'Automatischer Objektträger-Färber' },
  { id: 67, code: 'TG', name: 'Thermographie' },
  { id: 68, code: 'VA', name: 'Sehschärfe' },
  { id: 69, code: 'XC', name: 'Externe Kamera-Fotographie' }
];

const FreetextReporting = () => {
  const { t } = useTranslation();
  const { showSuccess, showError, showWarning } = useSnackbar();

  // Verfügbare Datenbankfelder aus taskColumns (ohne Blacklist)
  const DATABASE_FIELDS = [
    { key: '', label: t('admin.reportingManagement.freetextReporting.fieldEditor.noLink') },
    { key: 'patient_id', label: t('taskColumns.patient_id') },
    { key: 'patient_name', label: t('taskColumns.patient_name') },
    { key: 'birth_date', label: t('taskColumns.birth_date') },
    { key: 'sex', label: t('taskColumns.sex') },
    { key: 'modality', label: t('taskColumns.modality') },
    { key: 'study_description', label: t('taskColumns.study_description') },
    { key: 'clinical_info', label: t('taskColumns.clinical_info') },
    { key: 'question', label: t('taskColumns.question') },
    { key: 'accession_number', label: t('taskColumns.accession_number') },
    { key: 'referring_physician', label: t('taskColumns.referring_physician') },
    { key: 'admission_id', label: t('taskColumns.admission_id') },
    { key: 'comment', label: t('taskColumns.comment') },
    { key: 'scan_protocol', label: t('taskColumns.scan_protocol') },
    { key: 'first_reader', label: t('taskColumns.first_reader') },
    { key: 'patientsAge', label: t('taskColumns.patientsAge') },
    { key: 'patientsWeight', label: t('taskColumns.patientsWeight') },
    { key: 'patientsSize', label: t('taskColumns.patientsSize') },
    { key: 'occupation', label: t('taskColumns.occupation') },
    { key: 'additionalPatientsHistory', label: t('taskColumns.additionalPatientsHistory') },
    { key: 'admittingDiagnosesDescription', label: t('taskColumns.admittingDiagnosesDescription') }
  ];
  const [templates, setTemplates] = useState([]);
  const [examinations, setExaminations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editorError, setEditorError] = useState(''); // Lokaler Fehler nur für Dialog-Validierung
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState([]);
  const [showInactive, setShowInactive] = useState(false); // Show/hide inactive templates

  // Editor-States
  const [editorOpen, setEditorOpen] = useState(false);
  const [currentTemplate, setCurrentTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState({
    name: '',
    template_type: 'F',
    standard_for: { type: 'all', values: [] },
    fields_json: []
  });

  // Standard-für States
  const [standardFor, setStandardFor] = useState('all'); // 'all', 'modalities', 'examinations'
  const [selectedValues, setSelectedValues] = useState([]);
  const [modalityFilter, setModalityFilter] = useState(''); // Filter für Untersuchungen nach Modalität

  // Field Editor States
  const [fieldEditorOpen, setFieldEditorOpen] = useState(false);
  const [currentField, setCurrentField] = useState(null);
  const [currentFieldIndex, setCurrentFieldIndex] = useState(-1);
  const [fieldForm, setFieldForm] = useState({
    id: '',
    label: '',
    type: 'freetext', // 'freetext' oder 'database'
    database_field: '',
    width: 12,
    height: 3,
    snippet: '' // Snippet-Kurzname (z.B. "@snippet_name")
  });

  // Preview States
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);

  // Delete Dialog States
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deletingLoading, setDeletingLoading] = useState(false);

  // Snippets States
  const [availableSnippets, setAvailableSnippets] = useState([]);
  const [snippetsLoading, setSnippetsLoading] = useState(false);

  // Drag and Drop States
  const [isResizing, setIsResizing] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dropIndex, setDropIndex] = useState(null);
  const resizeData = useRef({
    fieldIndex: -1,
    direction: null, // 'width', 'height'
    startPos: { x: 0, y: 0 },
    startSize: { width: 0, height: 0 }
  });

  const apiBaseUrl = getApiBaseUrl();

  useEffect(() => {
    fetchTemplates();
    fetchExaminations();
    fetchSnippets();
  }, [showInactive]); // Re-fetch when showInactive changes

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      // Füge include_inactive Parameter hinzu
      const url = showInactive ? '/templates?include_inactive=true' : '/templates';
      const response = await apiFetch(url);
      if (response.ok) {
        const data = await response.json();
        const freetextTemplates = data.filter(t => t.template_type === 'F');
        setTemplates(freetextTemplates);
        // Reset selection when templates are refreshed
        setSelected([]);
      } else {
        console.error('Failed to load templates');
        showError(t('common.error'), 'ERR-FREETEXT-001');
      }
    } catch (e) {
      console.error('Network error loading templates:', e);
      showError(t('common.error'), 'ERR-FREETEXT-002');
    } finally {
      setLoading(false);
    }
  };

  const fetchExaminations = async () => {
    try {
      const response = await apiFetch('/admin/examinations');
      if (response.ok) {
        const data = await response.json();
        setExaminations(data);
      }
    } catch (e) {
      console.error('Fehler beim Laden der Untersuchungen:', e);
    }
  };

  const fetchSnippets = async () => {
    setSnippetsLoading(true);
    try {
      const response = await apiFetch('/snippets');
      if (response.ok) {
        const data = await response.json();
        setAvailableSnippets(data);
        console.log('✅ Snippets geladen:', data.length);
      }
    } catch (e) {
      console.error('Fehler beim Laden der Snippets:', e);
    } finally {
      setSnippetsLoading(false);
    }
  };

  // Template-Management
  const handleNewTemplate = () => {
    setCurrentTemplate(null);
    setTemplateForm({
      name: '',
      template_type: 'F',
      standard_for: { type: 'all', values: [] },
      fields_json: [],
      is_active: true // Neue Templates sind standardmäßig aktiv
    });
    setStandardFor('all');
    setSelectedValues([]);
    setEditorOpen(true);
  };

  const handleEditTemplate = (template) => {
    setCurrentTemplate(template);
    
    let parsedFields = [];
    let parsedStandardFor = { type: 'all', values: [] };
    
    try {
      parsedFields = typeof template.fields_json === 'string' 
        ? JSON.parse(template.fields_json) 
        : (template.fields_json || []);
    } catch (e) {
      console.error('Fehler beim Parsen der Felder:', e);
    }
    
    try {
      parsedStandardFor = typeof template.standard_for === 'string' 
        ? JSON.parse(template.standard_for) 
        : (template.standard_for || { type: 'all', values: [] });
    } catch (e) {
      console.error('Fehler beim Parsen der Standard-Zuordnung:', e);
    }

    setTemplateForm({
      name: template.name || '',
      template_type: template.template_type || 'F',
      standard_for: parsedStandardFor,
      fields_json: parsedFields,
      is_active: template.is_active ?? true // Lade is_active Status (default true wenn undefined)
    });
    
    setStandardFor(parsedStandardFor.type);
    setSelectedValues(parsedStandardFor.values || []);
    setEditorOpen(true);
  };

  const handleSaveTemplate = async () => {
    try {
      setEditorError('');

      // Client-side Validierung - bleibt im Dialog
      if (!templateForm.name.trim()) {
        setEditorError(t('admin.reportingManagement.freetextReporting.errors.templateNameRequired'));
        return;
      }

      const templateData = {
        ...templateForm,
        standard_for: {
          type: standardFor,
          values: selectedValues
        },
        created_by: 1, // TODO: Aktueller User
        is_active: templateForm.is_active ?? true // Sende is_active Status (default true)
      };

      console.log('=== SAVE TEMPLATE DEBUG ===');
      console.log('templateForm.fields_json:', templateForm.fields_json);
      console.log('templateData.fields_json:', templateData.fields_json);
      console.log('templateData as JSON:', JSON.stringify(templateData, null, 2));

      const url = currentTemplate 
        ? `${API_BASE}/templates/${currentTemplate.id}`
        : `${API_BASE}/templates/`;
      
      const method = currentTemplate ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify(templateData)
      });

      if (response.ok) {
        // Erfolg: Dialog schließen, dann Snackbar auf Hauptseite zeigen
        setEditorOpen(false);
        showSuccess(t('common.saveSuccess'));
        fetchTemplates();
      } else {
        // Backend-Fehler: Im Dialog anzeigen UND Snackbar
        const errorData = await response.json();
        const errorMsg = errorData.detail || t('common.error');
        setEditorError(errorMsg);
        console.error('Backend error saving template:', errorData);
        showError(errorMsg, 'ERR-FREETEXT-003');
      }
    } catch (error) {
      console.error('Network error saving template:', error);
      showError(t('common.error'), 'ERR-FREETEXT-004');
    }
  };

  // Selection Handlers
  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelected(filteredTemplates.map(template => template.id));
    } else {
      setSelected([]);
    }
  };

  const handleSelect = (templateId) => {
    setSelected(prev => 
      prev.includes(templateId) 
        ? prev.filter(id => id !== templateId)
        : [...prev, templateId]
    );
  };

  const handleDeleteTemplate = (templateId) => {
    const template = templates.find(t => t.id === templateId);
    setDeleteTarget(template);
    setDeleteDialogOpen(true);
  };

  const handleDeleteSelected = () => {
    if (selected.length === 1) {
      const selectedTemplate = templates.find(template => template.id === selected[0]);
      setDeleteTarget(selectedTemplate);
    } else {
      setDeleteTarget({ count: selected.length });
    }
    setDeleteDialogOpen(true);
  };

  const handleToggleTemplateStatus = async (templateId, currentStatus) => {
    try {
      const newStatus = !currentStatus;
      const response = await apiFetch(`/templates/${templateId}`, {
        method: 'PUT',
        body: JSON.stringify({
          is_active: newStatus
        })
      });

      if (response.ok) {
        showSuccess(
          newStatus
            ? t('admin.reportingManagement.freetextReporting.success.templateActivated')
            : t('admin.reportingManagement.freetextReporting.success.templateDeactivated')
        );
        fetchTemplates();
      } else {
        console.error('Failed to toggle template status');
        showError(t('admin.reportingManagement.freetextReporting.errors.toggleStatusFailed'), 'ERR-FREETEXT-007');
      }
    } catch (err) {
      console.error('Network error toggling template status:', err);
      showError(t('admin.reportingManagement.freetextReporting.errors.toggleStatusFailed'), 'ERR-FREETEXT-008');
    }
  };

  const handleCopyTemplates = async () => {
    if (selected.length === 0) {
      showError(t('admin.reportingManagement.freetextReporting.errors.noTemplatesSelected'), 'ERR-FREETEXT-009');
      return;
    }

    try {
      // Get current user ID
      let currentUserId = 1; // Fallback
      try {
        const userResponse = await apiFetch('/auth/me');
        if (userResponse.ok) {
          const userData = await userResponse.json();
          currentUserId = userData.id || userData.user_id || 1;
        }
      } catch (error) {
        console.warn('Could not fetch current user, using fallback ID');
      }

      const templatesToCopy = templates.filter(t => selected.includes(t.id));
      let successCount = 0;

      for (const template of templatesToCopy) {
        const copyData = {
          ...template,
          name: `${template.name} - Kopie`,
          is_active: true, // Kopie ist standardmäßig aktiv
          created_by: currentUserId // Aktueller Benutzer als Ersteller
        };
        delete copyData.id; // ID entfernen, damit Backend neue ID generiert
        delete copyData.created_by_name; // Entfernen, damit Backend es neu setzt

        const response = await apiFetch('/templates', {
          method: 'POST',
          body: JSON.stringify(copyData)
        });

        if (response.ok) {
          successCount++;
        } else {
          console.error('Failed to copy template:', template.name);
        }
      }

      if (successCount > 0) {
        if (successCount === 1) {
          showSuccess(t('admin.reportingManagement.freetextReporting.success.templateCopied', { name: templatesToCopy[0].name }));
        } else {
          showSuccess(t('admin.reportingManagement.freetextReporting.success.templatesCopied', { count: successCount }));
        }
        setSelected([]);
        fetchTemplates();
      }
    } catch (error) {
      console.error('Error copying templates:', error);
      showError(t('common.error'), 'ERR-FREETEXT-010');
    }
  };

  // Filter templates based on search
  const filteredTemplates = templates.filter(template =>
    template.name.toLowerCase().includes(search.toLowerCase())
  );

  // Selection helpers
  const hasSelected = selected.length > 0;
  const exactlyOneSelected = selected.length === 1;

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;

    try {
      setDeletingLoading(true);

      const templatesToDelete = deleteTarget.count ? selected : [deleteTarget.id];
      let successCount = 0;
      let errors = [];

      for (const templateId of templatesToDelete) {
        try {
          const response = await fetch(`${API_BASE}/templates/${templateId}`, {
            method: 'DELETE',
            headers: { ...getAuthHeaders() }
          });

          if (response.ok) {
            successCount++;
          } else {
            const errorData = await response.json();
            errors.push(`Template ${templateId}: ${errorData.detail || t('common.error')}`);
            console.error(`Failed to delete template ${templateId}:`, errorData);
          }
        } catch (error) {
          errors.push(`Template ${templateId}: ${t('common.error')}`);
          console.error(`Network error deleting template ${templateId}:`, error);
        }
      }

      // Erfolg/Fehler Meldungen
      if (successCount > 0) {
        if (successCount === templatesToDelete.length) {
          showSuccess(templatesToDelete.length === 1 
            ? t('common.deleteSuccess') 
            : t('admin.reportingManagement.freetextReporting.deleteMultipleSuccess', { count: successCount }));
        } else {
          showWarning(t('admin.reportingManagement.freetextReporting.deletePartialSuccess', { 
            successCount, 
            totalCount: templatesToDelete.length 
          }));
          showError(errors.join(', '), 'ERR-FREETEXT-005');
        }
      } else {
        showError(errors.join(', '), 'ERR-FREETEXT-006');
      }

      setDeleteDialogOpen(false);
      setDeleteTarget(null);
      fetchTemplates();
      setSelected([]);
    } catch (error) {
      console.error('Critical error in delete operation:', error);
      showError(t('common.error'), 'ERR-FREETEXT-007');
    } finally {
      setDeletingLoading(false);
    }
  };

  // Field-Management
  const handleAddField = () => {
    setCurrentField(null);
    setCurrentFieldIndex(-1);
    setFieldForm({
      id: '',
      label: '',
      type: 'freetext',
      database_field: '',
      width: 12,
      height: 3,
      snippet: '' // Snippet bei neuem Feld leer
    });
    setFieldEditorOpen(true);
  };

  const handleEditField = (field, index) => {
    setCurrentField(field);
    setCurrentFieldIndex(index);
    setFieldForm({
      id: field.id || '',
      label: field.label || '',
      type: field.database_field ? 'database' : 'freetext',
      database_field: field.database_field || '',
      width: field.width || 12,
      height: field.height || 3,
      snippet: field.snippet || '' // Snippet laden beim Bearbeiten
    });
    setFieldEditorOpen(true);
  };

  const handleSaveField = () => {
    if (!fieldForm.label.trim()) {
      return;
    }

    console.log('=== SAVE FIELD DEBUG ===');
    console.log('fieldForm.snippet:', fieldForm.snippet);
    console.log('fieldForm:', fieldForm);

    const newField = {
      id: fieldForm.id || fieldForm.label.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, ''),
      label: fieldForm.label,
      type: 'textarea',
      width: fieldForm.width,
      height: fieldForm.height,
      required: false,
      database_field: fieldForm.type === 'database' ? fieldForm.database_field : '',
      default_value: ''
    };

    // Nur Snippet hinzufügen, wenn es nicht leer ist
    if (fieldForm.snippet && fieldForm.snippet.trim()) {
      newField.snippet = fieldForm.snippet.trim();
      console.log('✅ SNIPPET ADDED TO newField:', newField.snippet);
    } else {
      console.log('❌ NO SNIPPET - fieldForm.snippet was empty or undefined');
    }

    console.log('newField:', newField);
    console.log('newField.snippet:', newField.snippet);
    console.log('newField as JSON:', JSON.stringify(newField, null, 2));

    const updatedFields = [...templateForm.fields_json];
    
    if (currentFieldIndex >= 0) {
      updatedFields[currentFieldIndex] = newField;
    } else {
      updatedFields.push(newField);
    }

    console.log('updatedFields:', updatedFields);
    
    setTemplateForm({
      ...templateForm,
      fields_json: updatedFields
    });
    
    setFieldEditorOpen(false);
  };

  const handleDeleteField = (index) => {
    const updatedFields = templateForm.fields_json.filter((_, i) => i !== index);
    setTemplateForm({
      ...templateForm,
      fields_json: updatedFields
    });
  };

  // Field Size Management
  const updateFieldSize = (fieldIndex, newWidth, newHeight) => {
    const updatedFields = [...templateForm.fields_json];
    if (updatedFields[fieldIndex]) {
      updatedFields[fieldIndex] = {
        ...updatedFields[fieldIndex],
        width: Math.max(1, Math.min(12, newWidth)),
        height: Math.max(1, Math.min(10, newHeight))
      };
      
      setTemplateForm({
        ...templateForm,
        fields_json: updatedFields
      });
    }
  };

  const moveField = (fromIndex, toIndex) => {
    const updatedFields = [...templateForm.fields_json];
    const [movedField] = updatedFields.splice(fromIndex, 1);
    updatedFields.splice(toIndex, 0, movedField);
    
    setTemplateForm({
      ...templateForm,
      fields_json: updatedFields
    });
  };

  // Drag & Drop Event Handlers
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.target.outerHTML);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDropIndex(index);
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    
    if (draggedIndex === null || draggedIndex === dropIndex) {
      return;
    }

    moveField(draggedIndex, dropIndex);
    setDraggedIndex(null);
    setDropIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDropIndex(null);
  };





  // Drag and Drop Functions
  const handleResizeStart = (e, fieldIndex, direction) => {
    e.preventDefault();
    e.stopPropagation();
    
    const field = templateForm.fields_json[fieldIndex];
    if (!field) return;
    
    setIsResizing(true);
    
    resizeData.current = {
      fieldIndex,
      direction,
      startPos: { x: e.clientX, y: e.clientY },
      startSize: { width: field.width || 12, height: field.height || 3 }
    };

    document.addEventListener('mousemove', handleResizeMove, { passive: false });
    document.addEventListener('mouseup', handleResizeEnd, { passive: false });
    document.body.style.cursor = direction === 'width' ? 'ew-resize' : 'ns-resize';
    document.body.style.userSelect = 'none';
  };

  const handleResizeMove = (e) => {
    e.preventDefault();
    
    if (resizeData.current.fieldIndex === -1) return;
    
    const { fieldIndex, direction, startPos, startSize } = resizeData.current;
    const deltaX = e.clientX - startPos.x;
    const deltaY = e.clientY - startPos.y;
    
    if (direction === 'width') {
      const widthChange = Math.round(deltaX / 50);
      const newWidth = Math.max(1, Math.min(12, startSize.width + widthChange));
      
      if (newWidth !== templateForm.fields_json[fieldIndex]?.width) {
        updateFieldSize(fieldIndex, newWidth, startSize.height);
      }
    } else if (direction === 'height') {
      const heightChange = Math.round(deltaY / 25);
      const newHeight = Math.max(1, Math.min(10, startSize.height + heightChange));
      
      if (newHeight !== templateForm.fields_json[fieldIndex]?.height) {
        updateFieldSize(fieldIndex, startSize.width, newHeight);
      }
    }
  };

  const handleResizeEnd = () => {
    setIsResizing(false);
    resizeData.current = {
      fieldIndex: -1,
      direction: null,
      startPos: { x: 0, y: 0 },
      startSize: { width: 0, height: 0 }
    };

    document.removeEventListener('mousemove', handleResizeMove);
    document.removeEventListener('mouseup', handleResizeEnd);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  };

  // Cleanup beim unmount
  useEffect(() => {
    return () => {
      document.removeEventListener('mousemove', handleResizeMove);
      document.removeEventListener('mouseup', handleResizeEnd);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, []);

  const renderResizableField = (field, index) => {
    return (
      <Box
        className="resize-field"
        key={field.id}
        sx={{
          position: 'relative',
          backgroundColor: 'transparent',
          width: `${(field.width || 12) * 8.333}%`,
          marginBottom: 2,
          '&:hover': {
            '& .action-buttons': { opacity: 1 },
            '& .drag-handle': { opacity: 0.7 }
          }
        }}
      >
        {/* Titel über dem Feld */}
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
          {field.label}
        </Typography>

        {/* Hauptfeld mit Handles */}
        <Box sx={{ position: 'relative' }}>
          <TextField
            multiline
            rows={field.height || 3}
            fullWidth
            variant="outlined"
            disabled
            placeholder={field.database_field ? t('admin.reportingManagement.freetextReporting.fieldEditor.autoFilled', { field: field.database_field }) : t('admin.reportingManagement.freetextReporting.fieldEditor.freetextPlaceholder')}
            sx={{ 
              '& .MuiInputBase-root': { 
                backgroundColor: field.database_field 
                  ? (theme) => theme.palette.mode === 'dark' ? 'color-mix(in srgb, var(--rd-accent) 15%, transparent)' : 'color-mix(in srgb, var(--rd-primary) 12%, transparent)'
                  : (theme) => theme.palette.background.paper
              }
            }}
          />
          
          {/* Rechts-Handle für Breite */}
          <Box
            className="drag-handle"
            onMouseDown={(e) => handleResizeStart(e, index, 'width')}
            sx={{
              position: 'absolute',
              top: '50%',
              right: -2,
              width: 4,
              height: 40,
              backgroundColor: 'var(--rd-info)',
              cursor: 'ew-resize',
              borderRadius: '2px',
              transform: 'translateY(-50%)',
              opacity: 0,
              transition: 'opacity 0.2s',
              zIndex: 10,
              '&:hover': { 
                opacity: 1
              }
            }}
          />
          
          {/* Unten-Handle für Höhe */}
          <Box
            className="drag-handle"
            onMouseDown={(e) => handleResizeStart(e, index, 'height')}
            sx={{
              position: 'absolute',
              bottom: -2,
              left: '50%',
              width: 40,
              height: 4,
              backgroundColor: 'var(--rd-success)',
              cursor: 'ns-resize',
              borderRadius: '2px',
              transform: 'translateX(-50%)',
              opacity: 0,
              transition: 'opacity 0.2s',
              zIndex: 10,
              '&:hover': { 
                opacity: 1
              }
            }}
          />
        </Box>
        
        {/* Datenbankfeld-Info */}
        {field.database_field && (
          <Typography variant="caption" color="primary" sx={{ display: 'block', mt: 0.5 }}>
            🔗 {DATABASE_FIELDS.find(f => f.key === field.database_field)?.label || field.database_field}
          </Typography>
        )}
        
        {/* Größe-Info */}
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
          {field.height} {t('admin.reportingManagement.freetextReporting.fieldEditor.rows')}
        </Typography>
        
        {/* Action-Buttons */}
        <Box 
          className="action-buttons"
          sx={{
            position: 'absolute',
            top: 4,
            right: 4,
            display: 'flex',
            gap: 0.5,
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            borderRadius: 1,
            p: 0.5,
            opacity: 0,
            transition: 'opacity 0.2s'
          }}
        >
          {/* Feld bearbeiten */}
          <IconButton
            size="small"
            onClick={() => handleEditField(field, index)}
            sx={{ width: 24, height: 24 }}
          >
            <EditIcon sx={{ fontSize: 14 }} />
          </IconButton>
          
          {/* Feld löschen */}
          <IconButton
            size="small"
            onClick={() => handleDeleteField(index)}
            color="error"
            sx={{ width: 24, height: 24 }}
          >
            <DeleteIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Box>
      </Box>
    );
  };

  const renderTemplateList = () => (
    <>
      {/* Aktionsleiste */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box display="flex" alignItems="center" gap={2} flexWrap="wrap" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
            <Typography variant="subtitle2" sx={{ mr: 2 }}>
              {hasSelected
                                  ? t('admin.reportingManagement.freetextReporting.templatesSelected', { count: selected.length })
                : t('admin.reportingManagement.freetextReporting.errors.noTemplatesSelected')
              }
            </Typography>
            <Divider orientation="vertical" flexItem />
            <Tooltip title={t('common.edit')}>
              <span>
                <IconButton
                  size="small"
                  onClick={() => {
                    const template = templates.find(t => t.id === selected[0]);
                    handleEditTemplate(template);
                  }}
                  disabled={!exactlyOneSelected || loading || deletingLoading}
                >
                  <EditIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={t('admin.reportingManagement.freetextReporting.copyTemplateTooltip')}>
              <span>
                <IconButton
                  size="small"
                  onClick={handleCopyTemplates}
                  disabled={!hasSelected || loading || deletingLoading}
                >
                  <CopyIcon />
                </IconButton>
              </span>
            </Tooltip>
            {/* DELETE BUTTON VERSTECKT - Templates werden soft-deleted (aktiv/inaktiv) */}
            <Divider orientation="vertical" flexItem />
            <Tooltip title={t('common.preview')}>
              <span>
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => {
                    const template = templates.find(t => t.id === selected[0]);
                    setCurrentTemplate(template);
                    setPreviewOpen(true);
                  }}
                  disabled={!exactlyOneSelected || loading || deletingLoading}
                >
                  <PreviewIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
          
          {/* Such-Bereich */}
          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
            <TextField
              label={t('common.search')}
              value={search}
              onChange={e => setSearch(e.target.value)}
              size="small"
              sx={{ minWidth: 220, height: '40px', '& .MuiInputBase-root': { height: '40px' } }}
            />
            <Button
              variant={showInactive ? "contained" : "outlined"}
              startIcon={showInactive ? <VisibilityIcon /> : <VisibilityOffIcon />}
              onClick={() => setShowInactive(!showInactive)}
              color={showInactive ? undefined : "primary"}
              sx={showInactive 
                ? { backgroundColor: 'var(--rd-danger)', color: 'var(--rd-primary-contrast)', '&:hover': { backgroundColor: 'var(--rd-error)' }, height: '40px' } 
                : { height: '40px' }
              }
            >
              {showInactive ? t('admin.reportingManagement.freetextReporting.hideInactive') : t('admin.reportingManagement.freetextReporting.showInactive')}
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Tabelle */}
      <Paper sx={{ width: '100%' }}>
        <TableContainer sx={{ maxHeight: 'calc(100vh - 420px)' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" sx={{ position: 'sticky', top: 0, backgroundColor: (theme) => theme.palette.background.paper, zIndex: 2 }}>
                  <Checkbox
                    indeterminate={selected.length > 0 && selected.length < filteredTemplates.length}
                    checked={filteredTemplates.length > 0 && selected.length === filteredTemplates.length}
                    onChange={handleSelectAll}
                  />
                </TableCell>
                <TableCell sx={{ position: 'sticky', top: 0, backgroundColor: (theme) => theme.palette.background.paper, zIndex: 2 }}>{t('templates.name')}</TableCell>
                <TableCell sx={{ position: 'sticky', top: 0, backgroundColor: (theme) => theme.palette.background.paper, zIndex: 2 }}>{t('templates.standardFor.title')}</TableCell>
                <TableCell sx={{ position: 'sticky', top: 0, backgroundColor: (theme) => theme.palette.background.paper, zIndex: 2, width: '40%' }}>{t('templates.fields')}</TableCell>
                <TableCell sx={{ position: 'sticky', top: 0, backgroundColor: (theme) => theme.palette.background.paper, zIndex: 2 }}>Erstellt von</TableCell>
                <TableCell sx={{ position: 'sticky', top: 0, backgroundColor: (theme) => theme.palette.background.paper, zIndex: 2 }}>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : filteredTemplates.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    {search ? 'Keine Templates gefunden' : t('templates.noTemplates')}
                  </TableCell>
                </TableRow>
              ) : (
                filteredTemplates.map((template) => {
                  let standardForDisplay = '';
                  let standardForElement = null;
                  let fieldNames = [];
                  
                  try {
                    const standardFor = typeof template.standard_for === 'string' 
                      ? JSON.parse(template.standard_for) 
                      : template.standard_for;
                    
                    if (standardFor?.type === 'all') {
                      standardForDisplay = 'Alle';
                    } else if (standardFor?.type === 'examinations') {
                      const count = standardFor.values?.length || 0;
                      standardForDisplay = `${count} Untersuchungen`;
                    } else if (standardFor?.type === 'modalities') {
                      // Für Modalitäten zeigen wir Pills mit den Kürzeln
                      const modalityCodes = standardFor.values?.map(id => 
                        ALL_MODALITIES.find(m => m.id === id)?.code
                      ).filter(Boolean) || [];
                      
                      standardForElement = (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {modalityCodes.map((code, index) => (
                                                         <Chip
                               key={index}
                               label={code}
                               size="small"
                               color="primary"
                               variant="outlined"
                             />
                          ))}
                        </Box>
                      );
                    }
                    
                    const fields = typeof template.fields_json === 'string' 
                      ? JSON.parse(template.fields_json) 
                      : template.fields_json;
                    fieldNames = fields?.map(field => field.label) || [];
                  } catch (e) {
                    standardForDisplay = t('common.error');
                    fieldNames = [];
                  }

                  return (
                    <TableRow key={template.id} selected={selected.includes(template.id)}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selected.includes(template.id)}
                          onChange={() => handleSelect(template.id)}
                        />
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{template.name}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {standardForElement || standardForDisplay}
                      </TableCell>
                      <TableCell sx={{ width: '40%' }}>
                        {fieldNames.length > 0 ? (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {fieldNames.map((fieldName, index) => (
                              <Chip
                                key={index}
                                label={fieldName}
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                            ))}
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Keine Felder
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {template.created_by_name || t('common.unknown')}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Chip
                          label={template.is_active !== false ? 'Aktiv' : 'Inaktiv'}
                          size="small"
                          sx={template.is_active !== false
                            ? { backgroundColor: 'var(--rd-success)', color: 'var(--rd-primary-contrast)' }
                            : { backgroundColor: 'var(--rd-danger)', color: 'var(--rd-primary-contrast)' }
                          }
                          onClick={() => {
                            handleToggleTemplateStatus(template.id, template.is_active !== false);
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </>
  );

  const renderTemplateEditor = () => (
    <Dialog open={editorOpen} onClose={() => setEditorOpen(false)} maxWidth="xl" fullWidth>
      <DialogTitle>
        {currentTemplate ? t('templates.editTemplate') : t('templates.newTemplate')}
      </DialogTitle>
      <DialogContent>
        {editorError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setEditorError('')}>
            {editorError}
          </Alert>
        )}
        <Box sx={{ mt: 2, display: 'flex', gap: 3 }}>
          {/* Linke Seite - Konfiguration */}
          <Box sx={{ flex: 1, pr: 2, borderRight: (theme) => `1px solid ${theme.palette.divider}` }}>
            {/* Template Name */}
            <TextField
              label={t('admin.reportingManagement.freetextReporting.templateEditor.name')}
              value={templateForm.name}
              onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })}
              fullWidth
              required
              sx={{ mb: 2 }}
            />
            
            {/* Active/Inactive Toggle */}
            <FormControlLabel
              control={
                <Switch
                  checked={templateForm.is_active ?? true}
                  onChange={(e) => setTemplateForm({ ...templateForm, is_active: e.target.checked })}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: 'var(--rd-accent)',
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: 'var(--rd-accent)',
                    },
                  }}
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2">
                    {(templateForm.is_active ?? true) ? 'Template ist aktiv' : 'Template ist inaktiv'}
                  </Typography>
                  <Chip
                    label={(templateForm.is_active ?? true) ? 'Aktiv' : 'Inaktiv'}
                    size="small"
                    sx={(templateForm.is_active ?? true)
                      ? { backgroundColor: 'var(--rd-success)', color: 'var(--rd-primary-contrast)' }
                      : { backgroundColor: 'var(--rd-danger)', color: 'var(--rd-primary-contrast)' }
                    }
                  />
                </Box>
              }
              sx={{ mb: 3 }}
            />

            {/* Standard für */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" sx={{ mb: 2 }}>{t('admin.reportingManagement.freetextReporting.templateEditor.standardFor')}</Typography>
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={standardFor === 'all'}
                      onChange={() => {
                        setStandardFor('all');
                        setSelectedValues([]);
                        setModalityFilter('');
                      }}
                    />
                  }
                  label={t('admin.reportingManagement.freetextReporting.templateEditor.all')}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={standardFor === 'modalities'}
                      onChange={() => {
                        setStandardFor('modalities');
                        setSelectedValues([]);
                        setModalityFilter('');
                      }}
                    />
                  }
                  label={t('admin.reportingManagement.freetextReporting.templateEditor.modality')}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={standardFor === 'examinations'}
                      onChange={() => {
                        setStandardFor('examinations');
                        setSelectedValues([]);
                        setModalityFilter('');
                      }}
                    />
                  }
                  label={t('admin.reportingManagement.freetextReporting.templateEditor.examination')}
                />
              </Box>

              {/* Modalität/Untersuchung Auswahl */}
              {standardFor !== 'all' && (
                <Box>
                  {/* Modalitäts-Filter für Untersuchungen */}
                  {standardFor === 'examinations' && (
                    <FormControl fullWidth sx={{ mb: 2 }}>
                      <InputLabel>Nach Modalität filtern</InputLabel>
                      <Select
                        value={modalityFilter}
                        label="Nach Modalität filtern"
                        onChange={(e) => setModalityFilter(e.target.value)}
                      >
                        <MenuItem value="">Alle Modalitäten</MenuItem>
                        {ALL_MODALITIES.map((modality) => (
                          <MenuItem key={modality.id} value={modality.code}>
                            {modality.code} - {modality.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}

                  {/* Checkbox-Liste */}
                  <Paper variant="outlined" sx={{ p: 2, maxHeight: '300px', overflowY: 'auto' }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      {standardFor === 'modalities' 
                        ? `${selectedValues.length} von ${ALL_MODALITIES.length} ausgewählt`
                        : `${selectedValues.length} von ${examinations.filter(e => !modalityFilter || e.modality === modalityFilter).length} ausgewählt`
                      }
                    </Typography>
                    <Divider sx={{ mb: 1 }} />
                    {standardFor === 'modalities' ? (
                      // Modalitäten-Liste
                      ALL_MODALITIES.map((modality) => (
                        <FormControlLabel
                          key={modality.id}
                          control={
                            <Checkbox
                              checked={selectedValues.includes(modality.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedValues([...selectedValues, modality.id]);
                                } else {
                                  setSelectedValues(selectedValues.filter(id => id !== modality.id));
                                }
                              }}
                            />
                          }
                          label={`${modality.code} - ${modality.name}`}
                          sx={{ display: 'block', mb: 0.5 }}
                        />
                      ))
                    ) : (
                      // Untersuchungen-Liste (gefiltert)
                      examinations
                        .filter(exam => !modalityFilter || exam.modality === modalityFilter)
                        .map((exam) => (
                          <FormControlLabel
                            key={exam.id}
                            control={
                              <Checkbox
                                checked={selectedValues.includes(exam.id)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedValues([...selectedValues, exam.id]);
                                  } else {
                                    setSelectedValues(selectedValues.filter(id => id !== exam.id));
                                  }
                                }}
                              />
                            }
                            label={`${exam.short_code} - ${exam.name}`}
                            sx={{ display: 'block', mb: 0.5 }}
                          />
                        ))
                    )}
                  </Paper>
                </Box>
              )}
            </Box>

            {/* Feld hinzufügen Section */}
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">{t('admin.reportingManagement.freetextReporting.templateEditor.addFieldSection')}</Typography>
                <Button
                  className="button"
                  startIcon={<AddIcon />}
                  onClick={handleAddField}
                >
                  {t('common.add')}
                </Button>
              </Box>

              {/* Feldliste */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxHeight: '350px', overflowY: 'auto' }}>
                {templateForm.fields_json.map((field, index) => (
                  <Box key={field.id}>
                    {/* Orange Drop-Indikator Linie */}
                    {dropIndex === index && draggedIndex !== null && draggedIndex !== index && (
                      <Box
                        sx={{
                          height: '3px',
                          backgroundColor: 'var(--rd-accent)',
                          margin: '4px 0',
                          borderRadius: '2px',
                          boxShadow: '0 0 4px color-mix(in srgb, var(--rd-accent) 60%, transparent)'
                        }}
                      />
                    )}
                    
                    <Card 
                      variant="outlined" 
                      sx={{ 
                        p: 1,
                        cursor: 'grab',
                        boxShadow: draggedIndex === index ? 3 : 1,
                        opacity: draggedIndex === index ? 0.5 : 1,
                        border: dropIndex === index && draggedIndex !== null && draggedIndex !== index ? '2px solid var(--rd-accent)' : 'none',
                        '&:active': { cursor: 'grabbing' }
                      }}
                      draggable
                      onDragStart={(e) => handleDragStart(e, index)}
                      onDragOver={(e) => handleDragOver(e, index)}
                      onDrop={(e) => handleDrop(e, index)}
                      onDragEnd={handleDragEnd}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <DragIcon sx={{ color: 'grey.400', cursor: 'grab' }} />
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="subtitle2">
                            {field.label}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {field.database_field ? `${field.database_field}` : t('admin.reportingManagement.freetextReporting.fieldEditor.freetextField')} • {field.width}/12 × {field.height}
                          </Typography>
                        </Box>
                        <IconButton onClick={() => handleEditField(field, index)} size="small">
                          <EditIcon />
                        </IconButton>
                        <IconButton onClick={() => handleDeleteField(index)} size="small" color="error">
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </Card>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>

          {/* Rechte Seite - Interaktive Vorschau */}
          <Box sx={{ flex: 1, pl: 2 }}>
            <Box sx={{ 
              border: '2px solid var(--rd-accent)', 
              borderRadius: 1, 
              p: 3, 
              height: '75vh',
              minHeight: '600px',
              backgroundColor: (theme) => theme.palette.background.default,
              overflow: 'auto'
            }}>
              <Typography variant="h6" sx={{ mb: 3 }}>
                {t('common.preview')}
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                {templateForm.fields_json.map((field, index) => 
                  renderResizableField(field, index)
                )}
                
                {templateForm.fields_json.length === 0 && (
                  <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                    {t('admin.reportingManagement.freetextReporting.templateEditor.noFieldsAdded')}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setEditorOpen(false)} className="button-cancel">
          {t('common.cancel')}
        </Button>
        <Button 
          onClick={handleSaveTemplate} 
          className="button"
        >
          {t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );

  const renderFieldEditor = () => (
    <Dialog open={fieldEditorOpen} onClose={() => setFieldEditorOpen(false)} maxWidth="sm" fullWidth>
      <DialogTitle>
        {currentField ? t('admin.reportingManagement.freetextReporting.fieldEditor.editField') : t('admin.reportingManagement.freetextReporting.fieldEditor.addField')}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {/* Bezeichnung */}
          <TextField
            label={t('admin.reportingManagement.freetextReporting.fieldEditor.label')}
            value={fieldForm.label}
            onChange={(e) => setFieldForm({ ...fieldForm, label: e.target.value })}
            fullWidth
            required
            sx={{ mb: 3 }}
            helperText={t('admin.reportingManagement.freetextReporting.fieldEditor.labelHelperText')}
          />

          {/* Feldtyp */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>{t('admin.reportingManagement.freetextReporting.fieldEditor.type')}</Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={fieldForm.type === 'freetext'}
                    onChange={() => setFieldForm({ ...fieldForm, type: 'freetext', database_field: '' })}
                  />
                }
                label={t('admin.reportingManagement.freetextReporting.fieldEditor.freetextField')}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={fieldForm.type === 'database'}
                    onChange={() => setFieldForm({ ...fieldForm, type: 'database' })}
                  />
                }
                label={t('admin.reportingManagement.freetextReporting.fieldEditor.databaseField')}
              />
            </Box>
          </Box>

          {/* Datenbankverknüpfung */}
          {fieldForm.type === 'database' && (
            <Autocomplete
              options={DATABASE_FIELDS}
              getOptionLabel={(option) => option.label}
              value={DATABASE_FIELDS.find(f => f.key === fieldForm.database_field) || DATABASE_FIELDS[0]}
              onChange={(event, newValue) => {
                setFieldForm({ ...fieldForm, database_field: newValue?.key || '' });
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('admin.reportingManagement.freetextReporting.fieldEditor.databaseLink')}
                  sx={{ mb: 3 }}
                />
              )}
            />
          )}

          {/* Snippet einfügen - nur für Freitextfelder */}
          {fieldForm.type === 'freetext' && (
            <Autocomplete
              freeSolo
              options={availableSnippets}
              getOptionLabel={(option) => {
                if (typeof option === 'string') return option;
                // Verwende label mit @ Präfix, falls vorhanden
                if (option.label && option.label.trim()) {
                  return option.label.startsWith('@') ? option.label : `@${option.label}`;
                }
                // Fallback auf Name falls label leer
                return option.name || option.id || '';
              }}
              filterOptions={(options, { inputValue }) => {
                const searchLower = inputValue.toLowerCase();
                return options.filter(option => {
                  const label = (option.label || '').toLowerCase();
                  const name = (option.name || '').toLowerCase();
                  const content = (option.content || '').toLowerCase();
                  
                  // Suche in Label, Name und Content
                  return label.includes(searchLower) || 
                         name.includes(searchLower) || 
                         content.includes(searchLower);
                });
              }}
              value={
                // Finde das passende Snippet-Objekt oder verwende den String-Wert
                fieldForm.snippet 
                  ? availableSnippets.find(s => 
                      `@${s.label}` === fieldForm.snippet || 
                      s.label === fieldForm.snippet
                    ) || fieldForm.snippet
                  : ''
              }
              onChange={(event, newValue) => {
                if (typeof newValue === 'string') {
                  setFieldForm({ ...fieldForm, snippet: newValue });
                } else if (newValue && newValue.label) {
                  // Füge @ hinzu falls nicht vorhanden
                  const snippetValue = newValue.label.startsWith('@') 
                    ? newValue.label 
                    : `@${newValue.label}`;
                  setFieldForm({ ...fieldForm, snippet: snippetValue });
                } else {
                  setFieldForm({ ...fieldForm, snippet: '' });
                }
              }}
              inputValue={fieldForm.snippet || ''}
              onInputChange={(event, newInputValue, reason) => {
                // Nur bei manueller Eingabe (nicht bei "reset")
                if (reason === 'input') {
                  setFieldForm({ ...fieldForm, snippet: newInputValue });
                }
              }}
              renderOption={(props, option) => (
                <Box component="li" {...props} key={option.id} sx={{ display: 'block !important', py: 1 }}>
                  <Typography variant="body2" fontWeight="bold" sx={{ mb: 0.5, width: '100%' }}>
                    {option.name || option.label}
                  </Typography>
                  <Typography 
                    variant="caption" 
                    color="text.secondary" 
                    sx={{ 
                      display: 'block',
                      whiteSpace: 'normal',
                      lineHeight: 1.3,
                      width: '100%'
                    }}
                  >
                    {option.content?.substring(0, 100)}{option.content?.length > 100 ? '...' : ''}
                  </Typography>
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Snippet einfügen (optional)"
                  placeholder="Suche nach @Label, Name oder Inhalt..."
                  helperText="Snippet wird später im Report Editor automatisch ersetzt"
                />
              )}
              loading={snippetsLoading}
              sx={{ mb: 3 }}
            />
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setFieldEditorOpen(false)} className="button-cancel">
          {t('common.cancel')}
        </Button>
        <Button onClick={handleSaveField} className="button">
          {t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );

  const renderPreviewField = (field, index) => {
    return (
      <Box
        key={field.id}
        sx={{
          position: 'relative',
          border: (theme) => `1px solid ${theme.palette.divider}`,
          borderRadius: 1,
          p: 1,
          mb: 1,
          backgroundColor: field.database_field 
            ? (theme) => theme.palette.mode === 'dark' ? 'color-mix(in srgb, var(--rd-accent) 15%, transparent)' : 'color-mix(in srgb, var(--rd-primary) 12%, transparent)'
            : (theme) => theme.palette.background.paper,
          transition: 'all 0.2s ease',
          gridColumn: `span ${field.width}`,
          minHeight: field.height * 24 + 32,
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {/* Label */}
        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, fontWeight: 500 }}>
          {field.label}
        </Typography>
        
        {/* Feld-Inhalt */}
        <TextField
          multiline
          rows={field.height || 3}
          fullWidth
          variant="outlined"
          disabled
          placeholder={field.database_field ? t('admin.reportingManagement.freetextReporting.fieldEditor.autoFilled', { field: field.database_field }) : t('admin.reportingManagement.freetextReporting.fieldEditor.freetextPlaceholder')}
          sx={{ 
            flex: 1,
            '& .MuiInputBase-root': { 
              backgroundColor: field.database_field 
                ? (theme) => theme.palette.mode === 'dark' ? 'color-mix(in srgb, var(--rd-accent) 15%, transparent)' : 'color-mix(in srgb, var(--rd-primary) 12%, transparent)'
                : (theme) => theme.palette.background.paper
            }
          }}
        />
        
        {/* Datenbankfeld-Info */}
        {field.database_field && (
          <Typography variant="caption" color="primary" sx={{ display: 'block', mt: 0.5 }}>
            🔗 {DATABASE_FIELDS.find(f => f.key === field.database_field)?.label || field.database_field}
          </Typography>
        )}
        
        {/* Größe-Info */}
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
          {field.height} {t('admin.reportingManagement.freetextReporting.fieldEditor.rows')}
        </Typography>
      </Box>
    );
  };

  const renderPreview = () => {
    if (!currentTemplate) return null;
    
    let fields = [];
    try {
      fields = typeof currentTemplate.fields_json === 'string' 
        ? JSON.parse(currentTemplate.fields_json) 
        : (currentTemplate.fields_json || []);
    } catch (e) {
      fields = [];
    }

    return (
      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{t('common.preview')}: {currentTemplate.name}</DialogTitle>
        <DialogContent>
          <Box sx={{ 
            border: '2px solid var(--rd-accent)', 
            borderRadius: 1, 
            p: 3, 
            backgroundColor: (theme) => theme.palette.background.default,
            overflow: 'auto',
            maxHeight: '70vh',
            minHeight: '400px'
          }}>
            <Typography variant="h6" sx={{ mb: 3 }}>
              {t('common.preview')}
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
              {fields.map((field, index) => 
                renderPreviewField(field, index)
              )}
              
              {fields.length === 0 && (
                <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                  {t('admin.reportingManagement.freetextReporting.templateEditor.noFieldsAdded')}
                </Typography>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)} className="button-cancel">
            {t('common.close')}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FreetextIcon color="primary" />
          {t('admin.reportingManagement.freetextReporting.title')}
        </Typography>
        <Button
          className="button"
          startIcon={<AddIcon />}
          onClick={handleNewTemplate}
          disabled={loading}
        >
          {t('templates.newTemplate')}
        </Button>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t('admin.reportingManagement.freetextReporting.description')}
      </Typography>

      {/* Templates Liste */}
      {loading ? (
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress />
        </Box>
      ) : (
        renderTemplateList()
      )}

      {/* Dialoge */}
      {renderTemplateEditor()}
      {renderFieldEditor()}
      {renderPreview()}

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => !deletingLoading && setDeleteDialogOpen(false)}>
        <DialogTitle>
          {t('common.delete')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {deleteTarget && (
              deleteTarget.count 
                ? t('admin.reportingManagement.freetextReporting.deleteConfirm.multiple', { count: deleteTarget.count })
                : t('admin.reportingManagement.freetextReporting.deleteConfirm.single', { 
                    name: deleteTarget.name || 'Template'
                  }).replace(/'{name}'/, `"${deleteTarget.name || 'Template'}"`)
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDeleteDialogOpen(false)} 
            className="button-cancel"
            disabled={deletingLoading}
          >
            {t('common.cancel')}
          </Button>
          <Button 
            onClick={handleConfirmDelete} 
            className="button-delete"
            disabled={deletingLoading}
            startIcon={deletingLoading ? <CircularProgress size={20} /> : null}
          >
            {deletingLoading ? t('common.deleting') : t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FreetextReporting; 